import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import {
  ClipboardList, Plus, Loader2, Sparkles, Trash2, Pencil, Link2, BarChart3, GripVertical,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Q_TYPES = {
  text: "Texto libre",
  single: "Opción única",
  multi: "Opción múltiple",
  rating: "Valoración 1-5",
  nps: "NPS 0-10",
};
const STATUS_META = {
  draft: { label: "Borrador", color: "bg-slate-500/15 text-slate-500" },
  active: { label: "Activa", color: "bg-emerald-500/15 text-emerald-600" },
  closed: { label: "Cerrada", color: "bg-red-500/15 text-red-500" },
};

export default function SurveysPage() {
  const { token } = useAuth();
  const headers = useCallback(() => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }), [token]);
  const [surveys, setSurveys] = useState([]);
  const [editing, setEditing] = useState(null);
  const [resultsFor, setResultsFor] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/surveys`, { headers: headers() });
      setSurveys((await r.json()).surveys || []);
    } catch {
      toast.error("Error cargando encuestas");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (s, status) => {
    await fetch(`${API}/surveys/${s.id}`, { method: "PUT", headers: headers(), body: JSON.stringify({ status }) });
    toast.success(status === "active" ? "Encuesta activada · comparte el enlace" : `Encuesta ${STATUS_META[status].label.toLowerCase()}`);
    load();
  };

  const copyLink = (s) => {
    navigator.clipboard.writeText(`${window.location.origin}/encuesta/${s.public_token}`);
    toast.success("Enlace público copiado");
  };

  const remove = async (id) => {
    await fetch(`${API}/surveys/${id}`, { method: "DELETE", headers: headers() });
    toast.success("Encuesta eliminada");
    load();
  };

  return (
    <div className="space-y-6" data-testid="surveys-page">
      <div className="rounded-2xl p-6 md:p-8 text-white" style={{ background: "linear-gradient(120deg, #431407, #c2410c 65%, #f97316)" }}>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-orange-200 text-xs font-semibold uppercase tracking-widest mb-2">
              <ClipboardList className="w-4 h-4" /> Surveys
            </div>
            <h1 className="text-3xl md:text-4xl font-black">Encuestas</h1>
            <p className="text-orange-100/90 mt-2 max-w-2xl text-sm">
              Crea, distribuye y analiza encuestas sin salir de la plataforma. Lógica condicional,
              enlace público y resultados en tiempo real. Diséñalas con IA en segundos.
            </p>
          </div>
          <Button onClick={() => setEditing({ title: "", description: "", questions: [] })} className="bg-white text-orange-900 hover:bg-orange-50 font-bold" data-testid="surveys-create-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva encuesta
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-600" /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {surveys.map((s) => (
            <Card key={s.id} className="hover:shadow-md transition-all" data-testid={`survey-card-${s.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-sm">{s.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{s.description}</p>
                  </div>
                  <Badge className={`text-[10px] border-0 flex-shrink-0 ${STATUS_META[s.status]?.color}`}>{STATUS_META[s.status]?.label}</Badge>
                </div>
                <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
                  <span>{(s.questions || []).length} preguntas</span>
                  <span className="font-semibold text-orange-600">{s.responses_count || 0} respuestas</span>
                  <span>{s.created_by}</span>
                </div>
                <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                  {s.status === "draft" && (
                    <Button size="sm" className="h-7 text-xs" onClick={() => setStatus(s, "active")} data-testid={`survey-activate-${s.id}`}>Activar</Button>
                  )}
                  {s.status === "active" && (
                    <>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyLink(s)} data-testid={`survey-copylink-${s.id}`}>
                        <Link2 className="w-3 h-3 mr-1" /> Copiar enlace
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setStatus(s, "closed")}>Cerrar</Button>
                    </>
                  )}
                  {s.status === "closed" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setStatus(s, "active")}>Reabrir</Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setResultsFor(s)} data-testid={`survey-results-${s.id}`}>
                    <BarChart3 className="w-3 h-3 mr-1" /> Resultados
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditing(s)} data-testid={`survey-edit-${s.id}`}><Pencil className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => remove(s.id)}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {surveys.length === 0 && (
            <div className="md:col-span-2 text-center py-12 text-sm text-muted-foreground">
              No hay encuestas todavía. Crea la primera (o pídesela a la IA ✨).
            </div>
          )}
        </div>
      )}

      {editing && <BuilderDialog survey={editing} headers={headers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {resultsFor && <ResultsDialog survey={resultsFor} headers={headers} onClose={() => setResultsFor(null)} />}
    </div>
  );
}

function BuilderDialog({ survey, headers, onClose, onSaved }) {
  const isNew = !survey.id;
  const [title, setTitle] = useState(survey.title || "");
  const [description, setDescription] = useState(survey.description || "");
  const [questions, setQuestions] = useState(survey.questions || []);
  const [aiGoal, setAiGoal] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const addQuestion = () => setQuestions((p) => [...p, { id: crypto.randomUUID(), type: "text", text: "", options: [], required: false, show_if: null }]);
  const updateQ = (i, patch) => setQuestions((p) => p.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const removeQ = (i) => setQuestions((p) => p.filter((_, idx) => idx !== i));

  const aiGenerate = async () => {
    if (!aiGoal.trim()) return toast.error("Describe el objetivo de la encuesta");
    setGenerating(true);
    try {
      const r = await fetch(`${API}/surveys/ai-generate`, {
        method: "POST", headers: headers(), body: JSON.stringify({ goal: aiGoal }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Error");
      setTitle(d.title);
      setDescription(d.description);
      setQuestions(d.questions.map((q) => ({ ...q, id: q.id || crypto.randomUUID() })));
      toast.success("Encuesta generada con IA · revísala y guarda");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    if (!title.trim()) return toast.error("Título obligatorio");
    setSaving(true);
    try {
      const url = isNew ? `${API}/surveys` : `${API}/surveys/${survey.id}`;
      const r = await fetch(url, {
        method: isNew ? "POST" : "PUT", headers: headers(),
        body: JSON.stringify({ title, description, questions }),
      });
      if (!r.ok) throw new Error("Error guardando");
      toast.success("Encuesta guardada");
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Preguntas previas tipo single para lógica condicional
  const conditionSources = (idx) => questions.slice(0, idx).filter((q) => q.type === "single" && q.options.length);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="survey-builder-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Nueva encuesta" : `Editar: ${survey.title}`}</DialogTitle>
          <DialogDescription>Diseña preguntas con lógica condicional o genera todo con IA</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 p-3 rounded-xl border border-orange-500/30 bg-orange-500/5">
          <Input placeholder='✨ Objetivo · ej: "medir la cultura de innovación del equipo"' value={aiGoal} onChange={(e) => setAiGoal(e.target.value)} data-testid="survey-ai-goal" />
          <Button variant="outline" onClick={aiGenerate} disabled={generating} data-testid="survey-ai-generate">
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1 text-orange-600" />} Generar con IA
          </Button>
        </div>

        <div className="space-y-3">
          <Input placeholder="Título de la encuesta *" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="survey-title-input" />
          <Textarea placeholder="Descripción / introducción para quien responde" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />

          {questions.map((q, i) => (
            <div key={q.id} className="p-3 rounded-xl border space-y-2" data-testid={`survey-question-${i}`}>
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-xs font-bold text-muted-foreground">P{i + 1}</span>
                <Input className="flex-1" placeholder="Texto de la pregunta" value={q.text} onChange={(e) => updateQ(i, { text: e.target.value })} />
                <Select value={q.type} onValueChange={(v) => updateQ(i, { type: v, options: ["single", "multi"].includes(v) ? (q.options.length ? q.options : ["Opción 1", "Opción 2"]) : [] })}>
                  <SelectTrigger className="w-36 flex-shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(Q_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => removeQ(i)}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
              </div>
              {["single", "multi"].includes(q.type) && (
                <div className="pl-8 space-y-1.5">
                  {q.options.map((o, oi) => (
                    <div key={oi} className="flex gap-1.5">
                      <Input className="h-8 text-sm" value={o} onChange={(e) => updateQ(i, { options: q.options.map((x, xi) => (xi === oi ? e.target.value : x)) })} />
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => updateQ(i, { options: q.options.filter((_, xi) => xi !== oi) })}><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => updateQ(i, { options: [...q.options, `Opción ${q.options.length + 1}`] })}>+ Opción</Button>
                </div>
              )}
              <div className="pl-8 flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Switch checked={q.required} onCheckedChange={(v) => updateQ(i, { required: v })} /> Obligatoria
                </label>
                {conditionSources(i).length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span>Mostrar solo si</span>
                    <Select
                      value={q.show_if?.question_id || "none"}
                      onValueChange={(v) => updateQ(i, { show_if: v === "none" ? null : { question_id: v, value: "" } })}
                    >
                      <SelectTrigger className="h-7 w-auto text-xs"><SelectValue placeholder="(siempre)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">(siempre visible)</SelectItem>
                        {conditionSources(i).map((cq) => (
                          <SelectItem key={cq.id} value={cq.id}>{cq.text?.slice(0, 40) || "Pregunta"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {q.show_if?.question_id && (
                      <>
                        <span>=</span>
                        <Select value={q.show_if.value || ""} onValueChange={(v) => updateQ(i, { show_if: { ...q.show_if, value: v } })}>
                          <SelectTrigger className="h-7 w-auto text-xs"><SelectValue placeholder="valor" /></SelectTrigger>
                          <SelectContent>
                            {(questions.find((x) => x.id === q.show_if.question_id)?.options || []).map((o) => (
                              <SelectItem key={o} value={o}>{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          <Button variant="outline" onClick={addQuestion} className="w-full" data-testid="survey-add-question">
            <Plus className="w-4 h-4 mr-1" /> Añadir pregunta
          </Button>
          <Button onClick={save} disabled={saving} className="w-full" data-testid="survey-save-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />} Guardar encuesta
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResultsDialog({ survey, headers, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/surveys/${survey.id}/results`, { headers: headers() })
      .then((r) => r.json())
      .then(setData)
      .catch(() => toast.error("Error cargando resultados"));
  }, [survey.id, headers]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="survey-results-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5 text-orange-600" /> {survey.title}</DialogTitle>
          <DialogDescription>{data ? `${data.total_responses} respuestas recibidas` : "Cargando..."}</DialogDescription>
        </DialogHeader>
        {!data ? (
          <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-600" /></div>
        ) : (
          <div className="space-y-5">
            {data.results.map((res, i) => {
              const q = res.question;
              return (
                <div key={q.id} className="space-y-2">
                  <p className="text-sm font-semibold">P{i + 1}. {q.text} <span className="text-xs font-normal text-muted-foreground">· {res.answered} resp.</span></p>
                  {res.counts && (
                    <div className="space-y-1.5">
                      {Object.entries(res.counts).sort((a, b) => b[1] - a[1]).map(([opt, count]) => {
                        const pct = res.answered ? Math.round((count / res.answered) * 100) : 0;
                        return (
                          <div key={opt} className="text-xs">
                            <div className="flex justify-between mb-0.5"><span>{opt}</span><span className="font-semibold">{count} ({pct}%)</span></div>
                            <div className="h-2 rounded-full bg-muted overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#f97316,#fb923c)" }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {res.average !== undefined && (
                    <div className="flex items-center gap-4">
                      <div className="text-center p-3 rounded-xl bg-orange-500/10">
                        <p className="text-2xl font-black text-orange-600">{q.type === "nps" && res.nps !== undefined ? res.nps : res.average}</p>
                        <p className="text-[10px] text-muted-foreground uppercase">{q.type === "nps" ? "NPS" : "Media"}</p>
                      </div>
                      <div className="flex-1 flex items-end gap-1 h-14">
                        {Object.entries(res.distribution || {}).map(([k, v]) => {
                          const max = Math.max(...Object.values(res.distribution));
                          return (
                            <div key={k} className="flex-1 flex flex-col items-center gap-0.5">
                              <div className="w-full rounded-t bg-orange-400" style={{ height: `${(v / max) * 40}px` }} />
                              <span className="text-[9px] text-muted-foreground">{k}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {res.texts && (
                    <div className="space-y-1 max-h-36 overflow-y-auto">
                      {res.texts.map((t, ti) => (
                        <p key={ti} className="text-xs p-2 rounded-lg bg-muted">{t}</p>
                      ))}
                      {res.texts.length === 0 && <p className="text-xs text-muted-foreground">Sin respuestas de texto</p>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
